import React, { useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import ModifiedOrderSummaryComponent from "components/ModifiedOrderSummary/ModifiedOrderSummary";
import { useOrderModificationSummary } from "hooks/apiHooks";
import CircularLoader from "components/CircularLoader";
import ErrorState from "components/ErrorState/ErrorState";
import { isValidUuid } from "utils/commonUtils";
import { ORDER_PROPOSAL_STATUS } from "constants/config";

const ModifiedOrderSummary = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId");

  const proposedOrderFromState = location.state?.proposedOrder;

  const shouldFetchFallback =
    proposedOrderFromState == null && isValidUuid(eventId);

  const {
    data: summaryData,
    isPending,
    isFetching,
    error: summaryError,
  } = useOrderModificationSummary(eventId, {
    enabled: shouldFetchFallback,
  });

  const proposedOrderFromApi = useMemo(() => {
    if (!summaryData?.proposal_order) return undefined;

    const statusId = summaryData.proposal_order?.status?.id;

    if (statusId !== ORDER_PROPOSAL_STATUS.ACCEPTED) return undefined;

    return summaryData.proposal_order;
  }, [summaryData]);

  const proposedOrder = proposedOrderFromState ?? proposedOrderFromApi;

  if (!proposedOrderFromState && !isValidUuid(eventId)) {
    return (
      <ErrorState message="This page needs a valid order link. Open it from your order modification email, or include eventId in the URL." />
    );
  }

  if (shouldFetchFallback && (isPending || isFetching)) {
    return <CircularLoader />;
  }

  if (shouldFetchFallback && summaryError) {
    return <ErrorState message={summaryError?.message} />;
  }

  if (shouldFetchFallback && summaryData && !proposedOrderFromApi) {
    return (
      <ErrorState message="This order modification is not in an accepted state, or the details could not be loaded." />
    );
  }

  if (!proposedOrder) {
    return (
      <ErrorState message="We could not load your modified order summary. Try again from your email link." />
    );
  }

  return (
    <div>
      <ModifiedOrderSummaryComponent proposedOrder={proposedOrder} />
    </div>
  );
};

export default ModifiedOrderSummary;
